import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useOdAlumniApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

export default function OffboardingAlumniPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('OD') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const { fetching, data, pagination, columns: baseColumns, revokeConsent, setBoomerangBlock } = useOdAlumniApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    const actions = [];
    if (row.isActive) {
      actions.push({
        type: 'delete',
        label: t('offboardingAlumni.action.revokeConsent'),
        onClick: () => setConfirm({
          message: t('offboardingAlumni.confirm.revokeConsent', { person: row.personFullName }),
          onYes: () => revokeConsent(row.id, ''),
        }),
      });
    }
    if (row.boomerangBlocked) {
      actions.push({
        type: 'edit',
        label: t('offboardingAlumni.action.unblockBoomerang'),
        onClick: () => setConfirm({
          message: t('offboardingAlumni.confirm.unblockBoomerang', { person: row.personFullName }),
          onYes: () => setBoomerangBlock(row.id, false, ''),
        }),
      });
    } else {
      actions.push({
        type: 'delete',
        label: t('offboardingAlumni.action.blockBoomerang'),
        onClick: () => setConfirm({
          message: t('offboardingAlumni.confirm.blockBoomerang', { person: row.personFullName }),
          onYes: () => setBoomerangBlock(row.id, true, t('offboardingAlumni.defaultBlockReason')),
        }),
      });
    }
    return actions;
  }, [t, writable, revokeConsent, setBoomerangBlock]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      {useMemo(() => (
        <DataTable
          title={t('offboardingAlumni.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [t, fetching, columns, data, pagination, filterList, handleFilterConfirm, handleDataStateChange])}
      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
